import { TalkingAvatarToggleComponent } from '../conversation-input/talking-avatar-toggle/talking-avatar-toggle.component';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { VoiceEmotion } from '@heygen/streaming-avatar';
import { Subscription } from 'rxjs';

import { TLanguage } from '@core/interfaces/TBot';
import { TBotItem, UiConfiguration } from '@core/interfaces/TBotItem';
import { TSourceItem } from '@core/interfaces/TSourceItem';

import { TalkingAvatarService } from '../../../../../routes/bots/talking-avatar/talking-avatar.service';
import { ConversationDataService } from '../../../services/conversation-data.service';
import { IInputTransferData } from '../../../types/dtos';
import { BotsService } from '@shared/services/bots.service';
import { CommonService } from '@shared/services/common.service';
import { KeywordsService, SelectedKeyword } from '@shared/services/keywords.service';
import { LocalSourcesService } from '@shared/services/local-sources.service';
import { MetadataService, MetadataSlugs } from '@shared/services/metadata.service';
import { MultiSummaryService } from '@shared/services/multi-summary.service';

import { ConversationFiltersComponent } from '../conversation-filters/conversation-filters.component';
import { SpeechRecognitionComponent } from '../conversation-input/speech-recognition/speech-recognition.component';
import { CustomButtonComponent } from '../message-controls/custom-button/custom-button.component';
import { PromptHintsDropdownComponent } from '../prompt-hints-dropdown/prompt-hints-dropdown.component';
import { SourceSearchModalComponent } from '../source-search-modal/source-search-modal.component';

type FileErrorType = 'size' | 'ext' | 'maxInput';

interface ConversationFilters {
  _id?: string | null;
  language?: TLanguage | null;
  countries?: string[] | null;
  start?: string | null;
  end?: string | null;
}

export type ConversationInputOptions = {
  allowAttachFile?: boolean;
  isExtended?: boolean;
  filterButton?: boolean;
  promptHints?: string[];
};

@Component({
  selector: 'app-conversation-input-dynamic',
  templateUrl: './conversation-input-dynamic.component.html',
  styleUrls: ['./conversation-input-dynamic.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CdkTextareaAutosize,
    MatIcon,
    ConversationFiltersComponent,
    CustomButtonComponent,
    PromptHintsDropdownComponent,
    SpeechRecognitionComponent,
    TalkingAvatarToggleComponent,
  ],
})
export class ConversationInputDynamicComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isWriting = false;
  @Input() conversationStarted = false;
  @Input() options: ConversationInputOptions = {
    allowAttachFile: true,
    isExtended: false,
    filterButton: false,
    promptHints: [],
  };
  @Input() uiConfiguration?: UiConfiguration;

  @Output() messageSubmitted = new EventEmitter<IInputTransferData>();
  @Output() writingAbort = new EventEmitter();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInputInput1') fileInputInput1!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInputInput2') fileInputInput2!: ElementRef<HTMLInputElement>;

  public messagesForm = this._fb.nonNullable.group({
    message: ['', [Validators.required, Validators.minLength(1)]],
  });
  protected filtersAreOpened = false;
  protected selectedSources: TSourceItem[] = [];
  protected defaultPlaceholder = 'Ask anything...';
  protected placeholder = this.defaultPlaceholder;
  protected isDragging = false;
  protected isDraggingInput1 = false;
  protected isDraggingInput2 = false;
  private dragCounter = 0;
  private dragCounterInput1 = 0;
  private dragCounterInput2 = 0;
  protected fileValidationParameters = {
    allowedExt: [
      'pdf',
      'txt',
      'csv',
      'docx',
      'pptx',
      'rtf',
      'html',
      'css',
      'png',
      'jpg',
      'xlsx',
      'xls',
    ],
    // Document-only extensions for Compare bots
    documentExt: ['pdf', 'txt', 'csv', 'docx', 'pptx', 'rtf', 'html', 'css', 'xlsx', 'xls'],
    maxInputFiles: 5,
    maxFileSizeMB: 100,
  };
  protected errorsDictionary: { [key in FileErrorType]: string } = {
    size: `file must be under ${this.fileValidationParameters.maxFileSizeMB}MB`,
    ext: 'unsupported extension',
    maxInput: 'maximum number of files reached',
  };
  protected preventAttachFile = false;
  protected isInputFocused = false;
  protected files: File[] = [];
  protected filesInput1: File[] = [];
  protected filesInput2: File[] = [];
  private _bot: TBotItem | null = null;
  // Cache files and message per bot ID so they persist when switching between bots
  private _botCache: Map<
    string,
    { files: File[]; filesInput1: File[]; filesInput2: File[]; message: string }
  > = new Map();
  protected hasSummary = false;
  private _subs: Subscription[] = [];
  private _filesErrors: Set<FileErrorType> = new Set<FileErrorType>();
  private readonly SHARED_SOURCES_KEY = 'global';
  private hasSubmittedFiles = false;
  protected currentFilters: ConversationFilters = {};
  // Aggregated prompt hints from all dashboard bots for use when no specific bot is set
  protected aggregatedPromptHints: string[] = [];
  protected selectedHintIndex = -1;
  protected selectedKeywords: SelectedKeyword[] = [];
  protected activeKeywords: SelectedKeyword[] = [];

  constructor(
    private _fb: FormBuilder,
    private _commonService: CommonService,
    private _multiSummaryService: MultiSummaryService,
    private _localSourcesService: LocalSourcesService,
    private _dialog: MatDialog,
    private _botsService: BotsService,
    private _conversationDataService: ConversationDataService,
    protected _talkingAvatarService: TalkingAvatarService,
    private _keywordsService: KeywordsService,
    private _metadataService: MetadataService
  ) {
    this._multiSummaryService
      .getVisibilityState()
      .subscribe(isVisible => (this.hasSummary = isVisible));
  }

  get bot(): TBotItem | null {
    return this._bot;
  }

  @Input() set bot(value: TBotItem | null) {
    const previousBotId = this._bot?._id;
    const newBotId = value?._id;

    // If switching to a different bot, save current state and load cached state for new bot
    if (previousBotId !== newBotId) {
      // Save current state to cache (if we have a previous bot)
      if (previousBotId) {
        this._botCache.set(previousBotId, {
          files: [...this.files],
          filesInput1: [...this.filesInput1],
          filesInput2: [...this.filesInput2],
          message: this.messagesForm.get('message')?.value || '',
        });
      }

      // Clear current state
      this.files = [];
      this.filesInput1 = [];
      this.filesInput2 = [];
      this.messagesForm.get('message')?.setValue('');

      // Restore state from cache if available for new bot
      if (newBotId && this._botCache.has(newBotId)) {
        const cached = this._botCache.get(newBotId)!;
        this.files = [...cached.files];
        this.filesInput1 = [...cached.filesInput1];
        this.filesInput2 = [...cached.filesInput2];
        this.messagesForm.get('message')?.setValue(cached.message);
      }
    }

    this._bot = value;
  }

  @Input() set message(msg: string) {
    this.updateMessage(msg);
  }

  get filesLeftQty(): number {
    return this.fileValidationParameters.maxInputFiles - this.files.length;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.initialSubmissionHadFiles &&
      changes.initialSubmissionHadFiles.currentValue === true &&
      !changes.initialSubmissionHadFiles.firstChange
    ) {
      this.hasSubmittedFiles = true;
    }
  }

  ngOnInit() {
    this._filesErrors.clear();
    this.dragCounter = 0;
    this._subs.push(
      this._localSourcesService.getItems(this.SHARED_SOURCES_KEY).subscribe(sources => {
        this.selectedSources = sources;
      })
    );

    // Subscribe to all bots to aggregate prompt hints for dashboard use
    this._subs.push(
      this._botsService.getBotsObservable().subscribe(bots => {
        this.aggregatePromptHints(bots);
      })
    );

    // Load selected keywords from metadata
    this.loadSelectedKeywords();

    // Subscribe to conversation changes to load keywords from latest message
    this._subs.push(
      this._conversationDataService.conversation$.subscribe(conversation => {
        // Only load keywords when conversation has an ID (loaded from history) and has messages
        if (conversation?.id && conversation.messages && conversation.messages.length > 0) {
          // Get keywords from the latest user message
          const lastUserMessage = conversation.getLastMessageByAuthor('user');
          if (lastUserMessage && lastUserMessage.keywords && lastUserMessage.keywords.length > 0) {
            // Convert string[] to SelectedKeyword[] and set as active
            this.activeKeywords = lastUserMessage.keywords.map((keyword: string) => ({
              keyword,
              displayText: keyword,
            }));
            this.setPlaceholder();
          } else if (!lastUserMessage?.keywords || lastUserMessage.keywords.length === 0) {
            // Clear active keywords if the loaded conversation has no keywords
            this.activeKeywords = [];
            this.setPlaceholder();
          }
        }
      })
    );
  }

  onFileSelected(event: any) {
    this.handleFileInput(event.target.files);
  }

  onFileSelectedForField(event: any, fieldType: 'input1' | 'input2') {
    // Block adding files to input2 if input1 is empty (when both inputs exist)
    if (fieldType === 'input2' && this.isInput2Disabled()) {
      this._commonService.openErrorSnackBar('Please add files to the first upload field first');
      event.target.value = '';
      return;
    }

    const files = event.target.files;
    if (files && files.length > 0) {
      this.handleFieldFileInput(Array.from(files), fieldType);
    }
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  }

  handleFileInput(files: File[]) {
    // Files are now allowed at any point in the conversation
    // For Quiz bots or when only textarea exists, route to first available upload field
    if (this.bot?.type === 'quizBot' && (this.hasInput1() || this.hasInput2())) {
      // Route to first available upload field for Quiz bots
      const targetField = this.hasInput1() ? 'input1' : 'input2';
      this.handleFieldFileInput(Array.from(files), targetField);
      this.isDragging = false;
      this.dragCounter = 0;
      return;
    }

    this._filesErrors.clear();

    if (this.files.length >= this.fileValidationParameters.maxInputFiles) {
      this._filesErrors.add('maxInput');
      this.showErrors();
      this.isDragging = false;
      this.dragCounter = 0;
      return;
    }

    let duplicatesSkipped = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Check for duplicates across ALL file arrays
      const allExistingFiles = [...this.files, ...this.filesInput1, ...this.filesInput2];
      const isDuplicate = allExistingFiles.some(
        existingFile =>
          existingFile.name === file.name &&
          existingFile.size === file.size &&
          existingFile.lastModified === file.lastModified
      );

      if (isDuplicate) {
        duplicatesSkipped++;
        continue;
      }

      if (this.validateFile(file)) {
        if (file) {
          this.files.push(file);
        }
      }
      if (this.filesLeftQty === 0) {
        if (files.length - i - 1 > 0) {
          this._filesErrors.add('maxInput');
        }
        break;
      }
    }

    // Show message if duplicates were skipped
    if (duplicatesSkipped > 0) {
      this._commonService.openErrorSnackBar(
        duplicatesSkipped === 1 ? 'File already added' : `${duplicatesSkipped} files already added`
      );
    }

    this.showErrors();
    this.isDragging = false;
    this.dragCounter = 0;
  }

  submit() {
    // Don't allow submission while AI is writing
    if (this.isWriting) {
      return;
    }

    // Use canSubmit() for validation - it handles all minFiles logic
    if (!this.canSubmit()) {
      // Show appropriate error message
      const tooltip = this.getSubmitButtonTooltip();
      if (tooltip !== 'Submit') {
        this._commonService.openErrorSnackBar(tooltip);
      }
      return;
    }

    // Collect all files from all sources
    const allFiles = [...this.files, ...this.filesInput1, ...this.filesInput2];

    if (allFiles.length > 0) {
      this.hasSubmittedFiles = true;
    }

    // Get message text: use textarea value if available, otherwise construct appropriate message
    let messageText = this.messagesForm.controls.message.value;

    // If no textarea or empty message, construct appropriate message based on field configuration
    if (!this.hasTextarea() || !messageText || messageText.trim() === '') {
      if (this.hasInput1() && this.hasInput2()) {
        // For bots with two upload fields, create a descriptive message with file names
        const input1Names = this.filesInput1.map(f => f.name).join(', ');
        const input2Names = this.filesInput2.map(f => f.name).join(', ');
        messageText = `Process ${input1Names} with ${input2Names}`;
      } else if (this.bot?.initialPrompt) {
        messageText = this.bot.initialPrompt;
      } else {
        messageText = 'File upload'; // Fallback
      }
    }

    // Prepare message data
    // If both input1 and input2 exist, send them as separate files and referenceFiles
    // Otherwise, combine all files into the files array
    const messageData: any = {
      message: messageText,
      sourceIds: this.selectedSources.map(source => source._id),
      project_id: this.currentFilters._id,
      language: this.currentFilters?.language,
      language_name: this.currentFilters?.language?.name,
      language_code: this.currentFilters?.language?.code,
      dateStart: this.currentFilters?.start,
      dateEnd: this.currentFilters?.end,
      countries: this.currentFilters?.countries,
      keywords: this.activeKeywords.length > 0 ? this.activeKeywords.map(k => k.keyword) : [],
    };

    // Handle files based on configuration
    // For quiz bots, always send all files in the files array (like the old component)
    if (this.bot?.type === 'quizBot') {
      messageData.files = allFiles;
    } else if (this.hasInput1() && this.hasInput2()) {
      // Both upload fields exist: send as files and referenceFiles
      messageData.files = [...this.files, ...this.filesInput1];
      messageData.referenceFiles = [...this.filesInput2];
    } else {
      // Single upload field or textarea with upload: combine all files
      messageData.files = allFiles;
    }

    this.messageSubmitted.emit(messageData);
    this.files = [];
    this.filesInput1 = [];
    this.filesInput2 = [];
    // Clear the cache for this bot so files don't come back after submission
    if (this.bot?._id) {
      this._botCache.delete(this.bot._id);
    }
    this.setPlaceholder();
    this.updateMessage('');
    // Don't clear activeKeywords - they persist throughout the conversation
    // User can manually deselect them if needed
    if (this.filtersAreOpened) {
      this.toggleFilters(false);
    }
  }

  removeFile(i: number) {
    this.files.splice(i, 1);
  }

  setPlaceholder(placeholder: string | null = null) {
    if (placeholder) {
      this.placeholder = placeholder;
    } else if (this.activeKeywords.length > 0) {
      const keywordsText = this.activeKeywords.map(k => k.displayText).join(', ');
      this.placeholder = `Ask about "${keywordsText}"...`;
    } else {
      this.placeholder = this.defaultPlaceholder;
    }
  }

  toggleFilters(force?: boolean) {
    this.filtersAreOpened = force ?? !this.filtersAreOpened;
  }

  protected get shouldShowFilters(): boolean {
    return !!this.options.filterButton || !!this.getField('textarea')?.showFilters;
  }

  openSourceSearch(): void {
    const dialogRef = this._dialog.open(SourceSearchModalComponent);
    dialogRef.componentInstance.botId = this.SHARED_SOURCES_KEY;
    dialogRef.componentInstance.sourceSelected.subscribe((source: TSourceItem) => {
      this._localSourcesService.addItem(this.SHARED_SOURCES_KEY, source);
      dialogRef.close();
    });
  }

  removeSource(source: TSourceItem): void {
    this._localSourcesService.removeItem(this.SHARED_SOURCES_KEY, source);
  }

  handleFilterChange(filters: ConversationFilters): void {
    this.currentFilters = filters;
  }

  ngOnDestroy() {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  protected updateMessage(msg: string | null) {
    if (msg === null) {
      this.messagesForm.controls.message.reset();
    } else {
      this.messagesForm.controls.message.patchValue(msg);
    }
  }

  protected validateFile(file: File): boolean {
    const fileExt = file.name.slice(((file.name.lastIndexOf('.') - 1) >>> 0) + 2);

    // For bots with both input1 and input2, only allow documents (no images)
    const hasTwoUploadFields = this.hasInput1() && this.hasInput2();
    const allowedExtensions = hasTwoUploadFields
      ? this.fileValidationParameters.documentExt
      : this.fileValidationParameters.allowedExt;

    if (!allowedExtensions.includes(fileExt)) {
      this._filesErrors.add('ext');
      return false;
    }
    if (file.size / 1024 > this.fileValidationParameters.maxFileSizeMB * 1024) {
      this._filesErrors.add('size');
      return false;
    }
    return true;
  }

  /**
   * Check if a file is a duplicate across all input fields
   */
  protected isDuplicateFile(file: File): boolean {
    // Check against ALL file arrays - prevent same file in different fields
    const allExistingFiles = [...this.files, ...this.filesInput1, ...this.filesInput2];
    return allExistingFiles.some(
      existingFile =>
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified
    );
  }

  protected showErrors() {
    if (this._filesErrors.size) {
      let error = 'Some files were not added due to errors: ';
      error += Array.from(this._filesErrors)
        .map(type => this.errorsDictionary[type])
        .join(', ')
        .concat('.');
      this._commonService.openErrorSnackBar(error);
    }
  }

  handleDragEnter(event: DragEvent, fieldType?: 'input1' | 'input2'): void {
    event.preventDefault();
    event.stopPropagation();

    if (fieldType === 'input1') {
      this.dragCounterInput1++;
      this.isDraggingInput1 = true;
    } else if (fieldType === 'input2') {
      // Don't show drag state if input2 is disabled
      if (this.isInput2Disabled()) {
        return;
      }
      this.dragCounterInput2++;
      this.isDraggingInput2 = true;
    } else {
      this.dragCounter++;
      this.isDragging = true;
    }
  }

  handleDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Allow file drops throughout the conversation
  }

  handleDragLeave(event: DragEvent, fieldType?: 'input1' | 'input2'): void {
    event.preventDefault();
    event.stopPropagation();

    if (fieldType === 'input1') {
      this.dragCounterInput1--;
      if (this.dragCounterInput1 === 0) {
        this.isDraggingInput1 = false;
      }
    } else if (fieldType === 'input2') {
      this.dragCounterInput2--;
      if (this.dragCounterInput2 === 0) {
        this.isDraggingInput2 = false;
      }
    } else {
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDragging = false;
      }
    }
  }

  handleDrop(event: DragEvent, fieldType?: 'input1' | 'input2'): void {
    event.preventDefault();
    event.stopPropagation();

    // Reset drag state when files are dropped
    if (fieldType === 'input1') {
      this.isDraggingInput1 = false;
      this.dragCounterInput1 = 0;
    } else if (fieldType === 'input2') {
      this.isDraggingInput2 = false;
      this.dragCounterInput2 = 0;
    } else {
      this.isDragging = false;
      this.dragCounter = 0;
    }
  }

  onDropFiles(event: DragEvent, fieldType?: 'input1' | 'input2'): void {
    this.handleDrop(event, fieldType);
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      if (fieldType) {
        this.handleFieldFileInput(Array.from(files), fieldType);
      } else {
        // For Quiz bots with upload fields but no specific field targeted, route to first available field
        if (this.bot?.type === 'quizBot' && (this.hasInput1() || this.hasInput2())) {
          const targetField = this.hasInput1() ? 'input1' : 'input2';
          this.handleFieldFileInput(Array.from(files), targetField);
        } else {
          this.handleFileInput(Array.from(files));
        }
      }
    }
  }

  /**
   * Check if input2 should be disabled
   * Input2 is disabled only when: both inputs exist, input1 is empty, AND there's no text in textarea
   * If user has text, they can upload to input2 directly (validate text against file)
   */
  protected isInput2Disabled(): boolean {
    const hasText = this.messagesForm.valid;
    // If there's text, input2 is always enabled (can validate text against file)
    if (hasText) {
      return false;
    }
    // Without text, require files in input1 first
    return this.hasInput1() && this.hasInput2() && this.filesInput1.length === 0;
  }

  protected handleFieldFileInput(files: File[], fieldType: 'input1' | 'input2'): void {
    this._filesErrors.clear();

    // Block adding files to input2 if input1 is empty (when both inputs exist)
    if (fieldType === 'input2' && this.isInput2Disabled()) {
      this._commonService.openErrorSnackBar('Please add files to the first upload field first');
      return;
    }

    const field = this.getField(fieldType);
    const maxFiles = field?.maxFiles || 5;
    const currentFiles = fieldType === 'input1' ? this.filesInput1 : this.filesInput2;

    if (currentFiles.length >= maxFiles) {
      this._filesErrors.add('maxInput');
      this.showErrors();
      return;
    }

    let duplicatesSkipped = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Check for duplicates first
      if (this.isDuplicateFile(file)) {
        duplicatesSkipped++;
        continue;
      }

      // Validate file (includes file type check for Compare bots)
      if (this.validateFile(file)) {
        if (file && currentFiles.length < maxFiles) {
          if (fieldType === 'input1') {
            this.filesInput1.push(file);
          } else {
            this.filesInput2.push(file);
          }
        }
      }
      if (currentFiles.length >= maxFiles) {
        if (files.length - i - 1 > 0) {
          this._filesErrors.add('maxInput');
        }
        break;
      }
    }

    // Show message if duplicates were skipped
    if (duplicatesSkipped > 0) {
      this._commonService.openErrorSnackBar(
        duplicatesSkipped === 1 ? 'File already added' : `${duplicatesSkipped} files already added`
      );
    }

    this.showErrors();
  }

  protected removeFieldFile(index: number, fieldType: 'input1' | 'input2'): void {
    if (fieldType === 'input1') {
      this.filesInput1.splice(index, 1);
    } else {
      this.filesInput2.splice(index, 1);
    }
  }

  protected getFieldFiles(fieldType: 'input1' | 'input2'): File[] {
    return fieldType === 'input1' ? this.filesInput1 : this.filesInput2;
  }

  areFilesAllowedToAttach(): boolean {
    // Allow file attachments throughout the conversation
    return this.options.allowAttachFile!;
  }

  get hasPromptHints(): boolean {
    const optionsHints = this.options.promptHints && this.options.promptHints.length > 0;
    const aggregatedHints = this.aggregatedPromptHints.length > 0;
    return optionsHints || aggregatedHints;
  }

  get shouldShowPromptHintsDropdown(): boolean {
    // Show dropdown before conversation starts (regardless of bot selection)
    return (
      this.hasPromptHints &&
      !this.isWriting &&
      this.messagesForm.get('message')?.value === '' &&
      !this.conversationStarted
    );
  }

  protected getEffectivePromptHints(): string[] {
    // If we have specific prompt hints from options (bot-specific), use those
    if (this.options.promptHints && this.options.promptHints.length > 0) {
      return this.options.promptHints;
    }
    // Otherwise, use aggregated hints from all bots (dashboard scenario)
    return this.aggregatedPromptHints;
  }

  protected selectPromptHint(hint: string): void {
    this.updateMessage(hint);
    this.selectedHintIndex = -1;
    // Keep focus on input to allow immediate typing
  }

  protected onInputFocus(): void {
    this.isInputFocused = true;
    this.selectedHintIndex = -1;
  }

  protected onInputBlur(): void {
    // Delay hiding to allow click events on dropdown items
    setTimeout(() => {
      this.isInputFocused = false;
      this.selectedHintIndex = -1;
    }, 150);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    // Handle special keys for dropdown navigation (only on dashboard)
    if (this.shouldShowPromptHintsDropdown && this.isInputFocused) {
      const hints = this.getEffectivePromptHints();

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedHintIndex = Math.min(this.selectedHintIndex + 1, hints.length - 1);
          return;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedHintIndex = Math.max(this.selectedHintIndex - 1, -1);
          return;
        case 'Enter':
          if (this.selectedHintIndex >= 0 && this.selectedHintIndex < hints.length) {
            event.preventDefault();
            this.selectPromptHint(hints[this.selectedHintIndex]);
            return;
          }
          // Handle regular enter behavior
          if (event.shiftKey) {
            // Allow shift+enter for new line
            return;
          } else {
            // Submit on enter
            event.preventDefault();
            this.submit();
            return;
          }
        case 'Escape':
          event.preventDefault();
          this.isInputFocused = false;
          this.selectedHintIndex = -1;
          return;
      }
    } else {
      // Handle regular enter behavior when dropdown is not visible
      if (event.key === 'Enter') {
        if (event.shiftKey) {
          // Allow shift+enter for new line
          return;
        } else {
          // Submit on enter
          event.preventDefault();
          this.submit();
          return;
        }
      }
    }
  }

  private aggregatePromptHints(bots: TBotItem[]): void {
    const allHints: string[] = [];

    bots.forEach(bot => {
      // Only include prompt hints from bots that are shown on dashboard
      if (bot.showOnDashboard && bot.promptHints && bot.promptHints.length > 0) {
        allHints.push(...bot.promptHints);
      }
    });

    // Remove duplicates, filter empty strings, and limit for performance
    const uniqueHints = [...new Set(allHints)]
      .filter(hint => hint && hint.trim().length > 0)
      .slice(0, 15); // Reduced to 15 for better UX

    this.aggregatedPromptHints = uniqueHints;
  }

  protected hasAssistantMessages(): boolean {
    const conversation = this._conversationDataService.currentValue;
    return conversation?.messages?.some(msg => msg.author === 'assistant') || false;
  }

  protected isTalkingAvatarActive(): boolean {
    return !this._talkingAvatarService.isClosed;
  }

  protected readConversationAloud(): void {
    const conversation = this._conversationDataService.currentValue;
    if (!conversation?.messages) {
      return;
    }

    // Get only assistant messages
    const assistantMessages = conversation.messages.filter(msg => msg.author === 'assistant');

    if (assistantMessages.length === 0) {
      this._commonService.openErrorSnackBar('No assistant messages to read aloud.');
      return;
    }

    // Combine all assistant messages into one text
    let fullText = assistantMessages
      .map(message => {
        // Use the same method as export to get clean text
        return conversation.getMessageToString(message, false); // false = no references for speech
      })
      .join('\n\n'); // Join messages with double line break

    // HeyGen API has a 5000 character limit, so we need to truncate if necessary
    const MAX_TEXT_LENGTH = 4800; // Leave some buffer
    let wasTruncated = false;

    if (fullText.length > MAX_TEXT_LENGTH) {
      wasTruncated = true;
      // Try to get the most recent messages first
      const reversedMessages = [...assistantMessages].reverse();
      let truncatedText = '';

      for (const message of reversedMessages) {
        const messageText = conversation.getMessageToString(message, false);
        const testText = messageText + (truncatedText ? '\n\n' + truncatedText : '');

        if (testText.length <= MAX_TEXT_LENGTH) {
          truncatedText = testText;
        } else {
          // If even this message is too long, take part of it
          if (!truncatedText) {
            truncatedText = messageText.substring(0, MAX_TEXT_LENGTH - 3) + '...';
          }
          break;
        }
      }

      fullText = truncatedText;
    }

    // Notify user if content was truncated
    if (wasTruncated) {
      this._commonService.openSnackBar(
        'Note: Only the most recent part of the conversation will be read aloud due to length limits.'
      );
    }

    // Set up proper recording session for results
    const lastAssistantMessage = conversation.getLastMessageByAuthor('assistant');

    if (conversation.id && lastAssistantMessage?._id) {
      this._talkingAvatarService.setRecordingIds(conversation.id, lastAssistantMessage._id);
    }

    // Set up talking avatar with current bot if available
    if (this.bot) {
      this._talkingAvatarService
        .setSettings(this.bot)
        .then(() => {
          this._talkingAvatarService.sendQuestion(fullText).catch(error => {
            console.error('Failed to start reading conversation:', error);
            this._commonService.openErrorSnackBar(
              'Failed to start reading conversation. Please check your internet connection and try again.'
            );
          });
        })
        .catch(error => {
          console.error('Failed to configure talking avatar:', error);
          this._commonService.openErrorSnackBar(
            'Failed to configure talking avatar. Please try again.'
          );
        });
    } else {
      // Set a default config if none exists
      if (!this._talkingAvatarService.config) {
        this._talkingAvatarService.config = {
          isAvailable: true,
          avatarId: 'Judy_Doctor_Sitting2_public',
          voiceRate: 1,
          voiceEmotion: VoiceEmotion.BROADCASTER,
        };
      }
      this._talkingAvatarService.sendQuestion(fullText).catch(error => {
        console.error('Failed to start reading conversation:', error);
        this._commonService.openErrorSnackBar(
          'Failed to start reading conversation. Please check your internet connection and try again.'
        );
      });
    }
  }

  private loadSelectedKeywords(): void {
    this._subs.push(
      this._metadataService.get<string>(MetadataSlugs.DashboardKeywords).subscribe({
        next: metadata => {
          if (metadata.value) {
            try {
              this.selectedKeywords = JSON.parse(metadata.value) as SelectedKeyword[];
            } catch (error) {
              console.error('Error parsing selected keywords:', error);
              this.selectedKeywords = [];
            }
          } else {
            this.selectedKeywords = [];
          }
        },
        error: () => {
          this.selectedKeywords = [];
        },
      })
    );
  }

  protected onKeywordClick(keyword: SelectedKeyword): void {
    // Toggle the keyword active state
    const isActive = this.activeKeywords.some(k => k.keyword === keyword.keyword);

    if (isActive) {
      // If keyword is active, remove it from active keywords
      this.activeKeywords = this.activeKeywords.filter(k => k.keyword !== keyword.keyword);
    } else {
      // If keyword is not active, add it to active keywords
      this.activeKeywords.push(keyword);
    }

    // Update placeholder to reflect the active keywords
    this.setPlaceholder();
  }

  protected isKeywordActive(keyword: SelectedKeyword): boolean {
    return this.activeKeywords.some(k => k.keyword === keyword.keyword);
  }

  protected getKeywordsToDisplay(): SelectedKeyword[] {
    // In conversation view (with bot), show active keywords
    // On dashboard (no bot), show selected keywords
    if (this.bot && this.activeKeywords.length > 0) {
      return this.activeKeywords;
    }
    return this.selectedKeywords;
  }

  protected get shouldShowKeywords(): boolean {
    // Show keywords in two scenarios:
    // 1. Dashboard (no conversation started) - show selected keywords for new conversations
    // 2. Active conversation - show active keywords being used
    if (!this.conversationStarted && this.selectedKeywords.length > 0) {
      return true; // Dashboard view
    }
    if (this.conversationStarted && this.activeKeywords.length > 0) {
      return true; // Conversation view with active keywords
    }
    return false;
  }

  protected showAvatarDisclaimer(): boolean {
    // Show disclaimer when:
    // 1. Avatar is not closed (active)
    // 2. Avatar is working or sending (talking)
    // 3. We have a bot (avatar is only available with specific bots)
    if (!this.bot || this._talkingAvatarService.isClosed) {
      return false;
    }

    // Check if avatar is currently working/talking
    // We need to access the private status property through any casting for now
    const service = this._talkingAvatarService as any;
    const isWorking = service.status?.status === 'working' || service.isSending;

    return isWorking;
  }

  /**
   * Clear active keywords - call this when starting a new conversation
   */
  public clearActiveKeywords(): void {
    this.activeKeywords = [];
    this.setPlaceholder();
  }

  // ============ UI Configuration Methods ============

  protected getVisibleFields(): UiInputField[] {
    if (!this.uiConfiguration?.inputFields) return [];
    return this.uiConfiguration.inputFields
      .filter(field => field.show)
      .sort((a, b) => a.order - b.order);
  }

  protected hasField(type: UiInputFieldType): boolean {
    const fields = this.getVisibleFields();
    return fields.some(field => field.type === type);
  }

  protected getField(type: UiInputFieldType): UiInputField | undefined {
    const fields = this.getVisibleFields();
    return fields.find(field => field.type === type);
  }

  protected hasTextarea(): boolean {
    return this.hasField('textarea');
  }

  protected hasInput1(): boolean {
    return this.hasField('input1');
  }

  protected hasInput2(): boolean {
    return this.hasField('input2');
  }

  /**
   * Check if form can be submitted based on validation rules
   */
  protected canSubmit(): boolean {
    // If writing, cannot submit
    if (this.isWriting) {
      return false;
    }

    const hasText = this.messagesForm.valid;
    const hasAnyFiles =
      this.files.length > 0 || this.filesInput1.length > 0 || this.filesInput2.length > 0;

    // If there's text in the textarea, allow submission (text is enough)
    if (this.hasTextarea() && hasText) {
      return true;
    }

    // If no textarea, require files
    if (!this.hasTextarea()) {
      return hasAnyFiles;
    }

    // With textarea but no text: allow if there are files
    return hasAnyFiles;
  }

  /**
   * Get tooltip text for submit button
   */
  protected getSubmitButtonTooltip(): string {
    const hasText = this.messagesForm.valid;
    const hasAnyFiles =
      this.files.length > 0 || this.filesInput1.length > 0 || this.filesInput2.length > 0;

    // If there's text, submission is allowed
    if (this.hasTextarea() && hasText) {
      return 'Submit';
    }

    // If no files and no text
    if (!hasAnyFiles && !hasText) {
      if (this.hasTextarea()) {
        return 'Please upload files or enter a message';
      }
      return 'Please upload files';
    }

    return 'Submit';
  }

  /**
   * Handle attach button click - route to appropriate field
   */
  protected handleAttachButtonClick(): void {
    // If bot has upload fields (input1/input2), route to first available field
    if (this.hasInput1() || this.hasInput2()) {
      const targetField = this.hasInput1() ? this.fileInputInput1 : this.fileInputInput2;
      targetField.nativeElement.click();
    } else {
      // Default behavior: use general file upload (shows below textarea)
      this.fileInput.nativeElement.click();
    }
  }
}

type UiInputFieldType = 'textarea' | 'input1' | 'input2';

interface UiInputField {
  id?: string;
  type: UiInputFieldType;
  order: number;
  show: boolean;
  required: boolean;
  label?: string;
  description?: string;
  buttonText?: string;
  maxFiles?: number; // Used as minimum required files (e.g., maxFiles=2 means need at least 2 files)
  showFilters?: boolean;
  showSourceSelector?: boolean;
  showUpload?: boolean;
  showFolderKeywords?: boolean;
  showAvatarSwitcher?: boolean;
  showVoiceOption?: boolean;
}
